/**
 * Data Import - Professional Excel upload interface for initial setup and ongoing data management.
 * Each data type can be uploaded independently with full traceability.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useIsCustomer } from '../hooks/usePortalMode';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Upload, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  Trash2, Database, Package, AlertTriangle, Building2, Truck,
  FileText, BarChart3, Users, Settings2, Clock, RefreshCw,
  History, ChevronRight, Warehouse, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { coreAPI } from '../lib/api';
import { useView } from '../contexts/ViewContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { API_BASE_URL } from '../lib/api/client';
import type { Site } from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { LucideIcon } from 'lucide-react';
import ImportDetailModal, { type DetailMode, type ImportStepLite } from './data-import/ImportDetailModal';

const API_BASE = `${API_BASE_URL}/data-import/sessions`;

interface ImportType {
  key: string;
  step: number;
  name: string;
  shortName: string;
  description: string;
  icon: LucideIcon;
  columns: string[];
  siteRequired: boolean;
  category: 'reference' | 'core' | 'transactional' | 'config';
}

const IMPORT_TYPES: ImportType[] = [
  { key: 'commodity', step: 1, name: 'Commodity Groups & Types', shortName: 'Commodities', description: 'Define commodity groups and types for spend categorisation and supplier mapping', icon: Package, columns: ['Commodity Group', 'Commodity Type'], siteRequired: false, category: 'reference' },
  { key: 'faultcodes', step: 2, name: 'Fault Code Categories', shortName: 'Fault Codes', description: 'Define fault groups and categories used in NCR classification', icon: AlertTriangle, columns: ['Fault Group', 'Fault Category'], siteRequired: false, category: 'reference' },
  { key: 'departments', step: 3, name: 'Departments', shortName: 'Departments', description: 'Define departments for user role assignment', icon: Settings2, columns: ['Department Name'], siteRequired: false, category: 'reference' },
  { key: 'warehouses', step: 4, name: 'Warehouses', shortName: 'Warehouses', description: 'Define warehouse/plant locations for stock planning and delivery routing', icon: Warehouse, columns: ['WHS_Code', 'WHS_Name', 'WHS_Type (optional)'], siteRequired: false, category: 'reference' },
  { key: 'suppliers', step: 5, name: 'Supplier Companies', shortName: 'Suppliers', description: 'Import supplier companies with site assignments, currency, and OTIF window settings', icon: Building2, columns: ['Customer', 'Supplier Group', 'Supplier Company Name', 'Supplier Code', 'Address', 'Currency', 'OTIF Days Early', 'OTIF Days Late', 'Company House Number', 'Spend'], siteRequired: false, category: 'core' },
  { key: 'supplier_additional', step: 6, name: 'Supplier Additional Details', shortName: 'Supplier Details', description: 'Extended supplier information including address, strategic status, commodity assignments, and KPI inclusion flags', icon: Building2, columns: ['Supplier Group', 'Supplier Name', 'Address Line 1', 'Address Line 2', 'Address Line 3', 'Post Code', 'Country', 'Website', 'Telephone', 'Strategic', 'Commodity Type', 'Commodity Group', 'Include in OTIF', 'Include in NCR/PPM', 'Include in OTD'], siteRequired: false, category: 'core' },
  { key: 'grns', step: 7, name: 'Goods Receipt Notes (GRNs)', shortName: 'GRNs', description: 'Historical delivery data — also auto-creates POs and PO lines if they don\'t exist', icon: Truck, columns: ['Order_No', 'GRN_No', 'Date_Received', 'Qty_Received', 'Qty_Advised', 'PO_LineNo', 'Part', 'Supplier', 'Due_Date', 'Price_Base', 'Price_Foreign', 'Currency_Code', 'Exchange_Rate', 'Buyer', 'Original_Due_Date', 'Del_Complete'], siteRequired: true, category: 'transactional' },
  { key: 'pos', step: 8, name: 'Purchase Orders (Open)', shortName: 'POs', description: 'Open purchase orders with line items, quantities, and pricing', icon: FileText, columns: ['PO_No', 'Supplier', 'Part', 'Due_Date', 'Qty_Ordered', 'Qty_Received', 'Price_Base', 'Price_Foreign', 'Currency_Code', 'Exchange_Rate', 'Line_No', 'Buyer', 'Original_Due_Date', 'Order_Type'], siteRequired: true, category: 'transactional' },
  { key: 'ncrs', step: 9, name: 'Non-Conformance Reports', shortName: 'NCRs', description: 'Quality issues linked to GRNs — includes rejection codes and fault classification', icon: AlertCircle, columns: ['Reject_Note_No', 'Part', 'Supplier', 'Order_No', 'GRN', 'PO_LineNo', 'Qty_Rejected', 'Rejection_Code', 'Reject_Code', 'Employee_Name', 'Buyer'], siteRequired: false, category: 'transactional' },
  { key: 'forecasts', step: 10, name: 'Demand Forecasts', shortName: 'Forecasts', description: 'Future demand data per part and supplier for forecast planning', icon: BarChart3, columns: ['Part_No', 'Supplier_Code', 'Converted_New_Qty_Exc_Scrap', 'Action_Date', 'Product_Grp', 'Part_Prim_Desc', 'Buying_UOM'], siteRequired: true, category: 'transactional' },
  { key: 'users', step: 11, name: 'User Accounts', shortName: 'Users', description: 'Create user accounts with site access, module permissions, and notification preferences', icon: Users, columns: ['Site', 'Name', 'Job Title', 'User name', 'TEAM', 'Access Orderbook', 'Access NCRs', 'Access Administrator', 'Notification Emails - Procurement', 'Notification Emails - Quality', 'Default login company type'], siteRequired: false, category: 'config' },
];

const CATEGORIES = [
  { key: 'reference', label: 'Reference Data', description: 'Set up before importing transactional data' },
  { key: 'core', label: 'Supplier Data', description: 'Import supplier companies and their details' },
  { key: 'transactional', label: 'Transactional Data', description: 'POs, deliveries, quality records, and forecasts' },
  { key: 'config', label: 'Configuration', description: 'User accounts and system setup' },
];

interface StepResult {
  step_number: number;
  status: string;
  site: number | null;
  site_name: string | null;
  file_name: string;
  rows_total: number;
  rows_created: number;
  rows_updated: number;
  rows_skipped: number;
  rows_failed: number;
  errors: string[];
  completed_at: string | null;
  started_at: string | null;
}

interface Session {
  id: number;
  status: string;
  current_step: number;
  created_by_name: string | null;
  created_at: string;
  steps: StepResult[];
}

export default function DataImport() {
  const isCustomer = useIsCustomer();
  if (!isCustomer) {
    return <Navigate to="/purchasing" replace />;
  }
  return <DataImportContent />;
}

function DataImportContent() {
  const { getViewQueryParams, viewLevel, selectedSiteId: viewSelectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const { isDataImportTileEnabled } = useMenuSettings();
  const visibleImportTypes = IMPORT_TYPES.filter(t => isDataImportTileEnabled(t.key));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [stepResults, setStepResults] = useState<Record<number, StepResult>>({});
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [uploading, setUploading] = useState<number | null>(null);
  const [uploadDialog, setUploadDialog] = useState<ImportType | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  // CR-5320 + CR-5321: drill-down modal state. mode controls which view
  // the modal renders; steps + stepFocus narrow the data shown.
  const [detail, setDetail] = useState<{
    mode: DetailMode;
    steps: ImportStepLite[];
    stepFocus?: 'created' | 'updated' | 'failed';
    title: string;
    description?: string;
  } | null>(null);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const token = localStorage.getItem('helix_auth_token') || sessionStorage.getItem('helix_auth_token') || '';
    const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '';
    return { 'Authorization': `Token ${token}`, 'X-CSRFToken': csrfToken };
  }, []);

  useEffect(() => {
    if (viewContextLoading) return;
    loadInitialData();
  }, [viewContextLoading, viewLevel, viewSelectedSiteId, selectedCompanyId, selectedGroupId]);

  // Recompute step results when site selection changes
  useEffect(() => {
    if (sessions.length > 0 && selectedSiteId) {
      updateStepResultsForSite(sessions, selectedSiteId);
    }
  }, [selectedSiteId, sessions]);

  // CR-5319 round 2: pulse Per-Company cards briefly when the site
  // selector changes so users get visible feedback that the per-site
  // tiles re-evaluated. Sarah's complaint was "no change on my side"
  // because most data types are Global by design and don't shift; we
  // animate the cards that *do* shift to draw the eye.
  const [sitePulseKey, setSitePulseKey] = useState(0);
  const previousSiteRef = useRef<string>('');
  useEffect(() => {
    if (selectedSiteId && previousSiteRef.current && previousSiteRef.current !== selectedSiteId) {
      setSitePulseKey(k => k + 1);
    }
    previousSiteRef.current = selectedSiteId;
  }, [selectedSiteId]);

  const selectedSiteName = sites.find(s => String(s.id) === selectedSiteId)?.name || '';

  const updateStepResultsForSite = (allSessions: Session[], siteId: string) => {
    // Build the latest completed step result per step_number for the selected company
    const resultMap: Record<number, StepResult> = {};
    const siteIdNum = parseInt(siteId, 10);

    // Flatten all steps across sessions, filter by site, pick latest per step_number
    const allSteps: (StepResult & { sessionId: number })[] = [];
    for (const sess of allSessions) {
      for (const step of sess.steps || []) {
        if (step.status === 'pending') continue;
        allSteps.push({ ...step, sessionId: sess.id });
      }
    }

    // For each step_number, find the latest completed step matching this site (or no site for global imports)
    for (const step of allSteps) {
      // Match: step has no site (global import like commodities) OR step's site matches selected site
      const matches = !step.site || step.site === siteIdNum;
      if (!matches) continue;

      const existing = resultMap[step.step_number];
      if (!existing || (step.completed_at && existing.completed_at && new Date(step.completed_at) > new Date(existing.completed_at))) {
        resultMap[step.step_number] = step;
      }
    }
    setStepResults(resultMap);

    // Find or create session for this site
    const active = allSessions.find((s: Session) => s.status === 'in_progress');
    if (active) {
      setSessionId(active.id);
    }
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load sites
      let loadedSiteId = '';
      try {
        const sitesRes = await coreAPI.getAccessibleSites();
        const sitesData = Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data as unknown as { results: Site[] }).results || [];
        setSites(sitesData);
        // CR-5319: preserve the user's current selection across refreshes.
        // Previously this always overwrote selectedSiteId with sitesData[0],
        // so clicking refresh "reset the filter to France" (or whichever
        // site happened to be first in the accessible list).
        if (sitesData.length > 0) {
          const currentMatches = selectedSiteId && sitesData.some(s => String(s.id) === selectedSiteId);
          loadedSiteId = currentMatches ? selectedSiteId : String(sitesData[0].id);
          if (loadedSiteId !== selectedSiteId) {
            setSelectedSiteId(loadedSiteId);
          }
        }
      } catch { /* optional */ }

      // Load all sessions for history, scoped by view context
      const viewParams = getViewQueryParams();
      const viewQuery = Object.entries(viewParams)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => `${k}=${v}`)
        .join('&');
      const sessionUrl = viewQuery ? `${API_BASE}/?${viewQuery}` : `${API_BASE}/`;
      const res = await fetch(sessionUrl, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const results: Session[] = data.results || data || [];
        setSessions(Array.isArray(results) ? results : []);

        // Populate step results for selected site
        if (loadedSiteId) {
          updateStepResultsForSite(results, loadedSiteId);
        }
      }
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const ensureSession = async (): Promise<number> => {
    if (sessionId) return sessionId;
    const res = await fetch(`${API_BASE}/`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error('Failed to create session');
    const data = await res.json();
    setSessionId(data.id);
    return data.id;
  };

  const handleUploadClick = (importType: ImportType) => {
    setUploadDialog(importType);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setSelectedFile(e.target.files[0]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) setSelectedFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!uploadDialog || !selectedFile) return;
    setUploading(uploadDialog.step);

    try {
      const sid = await ensureSession();
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (uploadDialog.siteRequired && selectedSiteId) {
        formData.append('site_id', selectedSiteId);
      }

      const res = await fetch(`${API_BASE}/${sid}/upload/${uploadDialog.step}/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const data = await res.json();

      if (res.ok) {
        setStepResults(prev => ({ ...prev, [uploadDialog.step]: data }));
        const msg = `${uploadDialog.shortName}: ${data.rows_created} created, ${data.rows_updated} updated`;
        if (data.rows_failed > 0) {
          toast.error(`${msg} — ${data.rows_failed} failed`);
        } else {
          toast.success(msg);
        }
        setUploadDialog(null);
        setSelectedFile(null);
        loadInitialData(); // Refresh history
      } else {
        toast.error(data.error || 'Import failed');
        if (data.step) setStepResults(prev => ({ ...prev, [uploadDialog.step]: data.step }));
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(null);
    }
  };

  // Stats
  const totalImports = Object.values(stepResults).filter(r => r.status === 'completed').length;
  const totalRowsCreated = Object.values(stepResults).reduce((sum, r) => sum + (r.rows_created || 0), 0);
  const totalErrors = Object.values(stepResults).reduce((sum, r) => sum + (r.rows_failed || 0), 0);
  const lastImport = Object.values(stepResults)
    .filter(r => r.completed_at)
    .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Data Import</h1>
          <p className="text-muted-foreground">Upload Excel files to set up and manage your platform data</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Global site selector */}
          {sites.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Importing for:</span>
              <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
                <SelectTrigger className="w-[250px]">
                  <SelectValue placeholder="Select company..." />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(site => (
                    <SelectItem key={site.id} value={String(site.id)}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button variant="outline" onClick={() => setShowHistory(!showHistory)}>
            <History className="h-4 w-4 mr-2" />
            {showHistory ? 'Hide History' : 'Import History'}
          </Button>
          <Button variant="outline" size="icon" onClick={loadInitialData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* CR-5319 round 2: explicit banner naming which tiles respond to
          the "Importing for" dropdown. Without this, switching the site
          selector looks like nothing happened — most data types are
          Global by design and stay identical across sites. */}
      <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p>
            The <span className="font-semibold">Importing for</span> selector only affects
            {' '}<span className="font-semibold">Per-Company</span> tiles
            (GRNs, Purchase Orders, Forecasts) and the
            {' '}<span className="font-semibold">Last Import</span> card.
            {' '}<span className="font-semibold">Global</span> tiles
            (Commodities, Fault Codes, Departments, Suppliers, NCRs, Users)
            apply across all companies and stay the same when you switch.
          </p>
        </div>
      </div>

      {/* Summary Stats — CR-5321: top three cards are now drill-down
          triggers. The fourth (Last Import) is informational only. */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <button
          type="button"
          className="text-left"
          disabled={totalImports === 0}
          onClick={() => setDetail({
            mode: 'types',
            steps: Object.values(stepResults).filter(s => s.status === 'completed'),
            title: 'Data types imported',
            description: 'Latest completed import per data type for the selected company.',
          })}
        >
          <Card className={totalImports > 0 ? 'cursor-pointer hover:border-primary/40 transition-colors' : 'opacity-90'}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Data Types Imported</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalImports}</div>
              <p className="text-xs text-muted-foreground">of {visibleImportTypes.length} available</p>
            </CardContent>
          </Card>
        </button>
        <button
          type="button"
          className="text-left"
          disabled={totalRowsCreated === 0}
          onClick={() => setDetail({
            mode: 'created',
            steps: Object.values(stepResults).filter(s => s.status === 'completed'),
            title: 'Records created',
            description: 'Per-import breakdown of created rows.',
          })}
        >
          <Card className={totalRowsCreated > 0 ? 'cursor-pointer hover:border-primary/40 transition-colors' : 'opacity-90'}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Records Created</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{totalRowsCreated.toLocaleString()}</div>
              {/* CR-5319: clarify scope so users don't read "across all
                  imports" as "every site". Globals are always counted; the
                  per-company portion is what changes with the dropdown. */}
              <p className="text-xs text-muted-foreground">selected company + global</p>
            </CardContent>
          </Card>
        </button>
        <button
          type="button"
          className="text-left"
          disabled={totalErrors === 0}
          onClick={() => setDetail({
            mode: 'errors',
            steps: Object.values(stepResults).filter(s => s.status !== 'pending'),
            title: 'Import errors',
            description: 'Rows that failed to import, grouped by data type.',
          })}
        >
          <Card className={totalErrors > 0 ? 'cursor-pointer hover:border-primary/40 transition-colors' : 'opacity-90'}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Errors</CardTitle>
              <AlertCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{totalErrors}</div>
              <p className="text-xs text-muted-foreground">rows with issues</p>
            </CardContent>
          </Card>
        </button>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Last Import</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {lastImport?.completed_at ? formatDistanceToNow(new Date(lastImport.completed_at), { addSuffix: true }) : '—'}
            </div>
            <p className="text-xs text-muted-foreground">{lastImport?.file_name || 'No imports yet'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Import History Table */}
      {showHistory && sessions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Import History
            </CardTitle>
            <CardDescription>All import sessions with who uploaded what and when</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data Type</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Failed</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  // CR-5319: sort History table rows newest-first so users
                  // can spot recent imports at a glance. Previously the
                  // rows came out in whatever order the API returned the
                  // sessions/steps, which produced nonsense like "19 days
                  // ago" appearing above "24 minutes ago". Also tag
                  // global (site-less) steps with a "Global" badge so the
                  // user can see *why* a row stays visible after they
                  // change the site selector.
                  const siteIdNum = selectedSiteId ? parseInt(selectedSiteId, 10) : null;
                  const allRows: Array<{ sess: Session; step: StepResult }> = [];
                  for (const sess of sessions) {
                    for (const step of sess.steps || []) {
                      if (step.status === 'pending') continue;
                      if (siteIdNum !== null && step.site && step.site !== siteIdNum) continue;
                      allRows.push({ sess, step });
                    }
                  }
                  allRows.sort((a, b) => {
                    // Site-scoped rows for the current site first, globals
                    // after. Within each group sort by completed_at desc.
                    const aGlobal = !a.step.site;
                    const bGlobal = !b.step.site;
                    if (aGlobal !== bGlobal) return aGlobal ? 1 : -1;
                    const at = a.step.completed_at ? new Date(a.step.completed_at).getTime() : 0;
                    const bt = b.step.completed_at ? new Date(b.step.completed_at).getTime() : 0;
                    return bt - at;
                  });
                  return allRows.map(({ sess, step }) => {
                    const importType = IMPORT_TYPES.find(t => t.step === step.step_number);
                    const isGlobal = !step.site;
                    return (
                      <TableRow key={`${sess.id}-${step.step_number}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {importType && <importType.icon className="h-4 w-4 text-muted-foreground" />}
                              <span className="font-medium text-sm">{importType?.shortName || `Step ${step.step_number}`}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {isGlobal ? (
                              <Badge variant="outline" className="text-[10px]">Global</Badge>
                            ) : (
                              step.site_name || <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{step.file_name || '—'}</TableCell>
                          <TableCell>
                            <Badge className={
                              step.status === 'completed' && step.rows_failed > 0 ? 'bg-amber-100 text-amber-800' :
                              step.status === 'completed' ? 'bg-green-100 text-green-800' :
                              step.status === 'failed' ? 'bg-red-100 text-red-800' :
                              step.status === 'skipped' ? 'bg-gray-100 text-gray-800' :
                              'bg-blue-100 text-blue-800'
                            }>
                              {step.status === 'completed' && step.rows_failed > 0 ? 'partial' : step.status}
                            </Badge>
                          </TableCell>
                          {/* CR-5320: Created / Updated / Failed cells
                              are drill-down triggers. Disabled when zero
                              so empty cells don't look interactive. */}
                          <TableCell>
                            <button
                              type="button"
                              disabled={(step.rows_created || 0) === 0}
                              onClick={() => setDetail({
                                mode: 'step',
                                steps: [step],
                                stepFocus: 'created',
                                title: `${importType?.shortName || 'Step'} — Created records`,
                                description: step.file_name || undefined,
                              })}
                              className={`text-sm font-medium ${(step.rows_created || 0) > 0 ? 'text-green-700 hover:underline cursor-pointer' : 'text-green-700 cursor-default'}`}
                            >
                              {step.rows_created || 0}
                            </button>
                          </TableCell>
                          <TableCell>
                            <button
                              type="button"
                              disabled={(step.rows_updated || 0) === 0}
                              onClick={() => setDetail({
                                mode: 'step',
                                steps: [step],
                                stepFocus: 'updated',
                                title: `${importType?.shortName || 'Step'} — Updated records`,
                                description: step.file_name || undefined,
                              })}
                              className={`text-sm ${(step.rows_updated || 0) > 0 ? 'text-blue-700 hover:underline cursor-pointer' : 'text-blue-700 cursor-default'}`}
                            >
                              {step.rows_updated || 0}
                            </button>
                          </TableCell>
                          <TableCell>
                            <button
                              type="button"
                              disabled={(step.rows_failed || 0) === 0 && (!step.errors || step.errors.length === 0)}
                              onClick={() => setDetail({
                                mode: 'step',
                                steps: [step],
                                stepFocus: 'failed',
                                title: `${importType?.shortName || 'Step'} — Failed rows`,
                                description: step.file_name || undefined,
                              })}
                              className={`text-sm ${(step.rows_failed || 0) > 0 ? 'text-red-700 hover:underline cursor-pointer font-semibold' : 'text-red-700 cursor-default'}`}
                            >
                              {step.rows_failed || 0}
                            </button>
                          </TableCell>
                          <TableCell className="text-sm">{sess.created_by_name || 'System'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {step.completed_at ? formatDistanceToNow(new Date(step.completed_at), { addSuffix: true }) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    });
                })()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Import Type Cards - Grouped by Category */}
      {CATEGORIES.map(cat => {
        const types = visibleImportTypes.filter(t => t.category === cat.key);
        if (types.length === 0) return null;
        return (
          <div key={cat.key}>
            <div className="mb-3">
              <h2 className="text-lg font-semibold">{cat.label}</h2>
              <p className="text-sm text-muted-foreground">{cat.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {types.map(type => {
                const result = stepResults[type.step];
                const Icon = type.icon;
                const hasResult = result && result.status !== 'pending';
                const hasErrors = hasResult && result.rows_failed > 0;
                const isSuccess = hasResult && result.status === 'completed' && !hasErrors;
                const isWarning = hasResult && result.status === 'completed' && hasErrors;
                const isFailed = hasResult && result.status === 'failed';

                // CR-5319 round 2: per-company cards get a stronger left
                // border + a brief flash on site change so the impact of
                // the dropdown is unmistakable. The flash is keyed on
                // sitePulseKey + type.key so React remounts the card
                // each time the site changes, restarting the animation.
                const perCompany = type.siteRequired;
                const cardClass = perCompany
                  ? 'group hover:shadow-md transition-all cursor-pointer border-l-4 border-l-primary'
                  : 'group hover:shadow-md hover:border-primary/30 transition-all cursor-pointer';
                const pulseClass = perCompany ? 'animate-in fade-in zoom-in-95 duration-300' : '';
                return (
                  <Card
                    key={`${type.key}-${perCompany ? sitePulseKey : 'g'}`}
                    className={`${cardClass} ${pulseClass}`}
                    onClick={() => handleUploadClick(type)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded-lg ${
                            isSuccess ? 'bg-green-100' :
                            isWarning ? 'bg-amber-100' :
                            isFailed ? 'bg-red-100' :
                            'bg-muted'
                          }`}>
                            <Icon className={`h-5 w-5 ${
                              isSuccess ? 'text-green-700' :
                              isWarning ? 'text-amber-700' :
                              isFailed ? 'text-red-700' :
                              'text-muted-foreground'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-sm">{type.name}</h3>
                            {/* CR-5319: tag every card so users can tell at a
                                glance which counts respond to the "Importing
                                for" dropdown and which don't. Without the
                                Global badge, switching sites looked like
                                "nothing changed" because most data types are
                                site-less and stayed identical. */}
                            <Badge
                              variant={type.siteRequired ? 'default' : 'outline'}
                              className={`text-[9px] px-1 py-0 mt-0.5 ${
                                type.siteRequired ? '' : 'text-muted-foreground'
                              }`}
                            >
                              {type.siteRequired
                                ? (selectedSiteName ? `For ${selectedSiteName}` : 'Per Company')
                                : 'Global'}
                            </Badge>
                          </div>
                        </div>
                        {isSuccess && (
                          <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                        )}
                        {isWarning && (
                          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
                        )}
                        {isFailed && (
                          <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
                        )}
                        {!hasResult && (
                          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{type.description}</p>

                      {hasResult ? (
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex gap-3">
                            <span className="text-green-700 font-medium">{result.rows_created} created</span>
                            {result.rows_updated > 0 && <span className="text-blue-700">{result.rows_updated} updated</span>}
                            {result.rows_failed > 0 && <span className="text-red-700">{result.rows_failed} failed</span>}
                          </div>
                          <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={(e) => { e.stopPropagation(); handleUploadClick(type); }}>
                            Re-upload
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {/* CR-5319 round 2: spell out *for which site*
                                a Per-Company tile has no data, so the
                                user understands the empty state changes
                                with the dropdown. */}
                            {perCompany && selectedSiteName
                              ? `Not imported for ${selectedSiteName}`
                              : 'Not imported yet'}
                          </span>
                          <Button variant="outline" size="sm" className="h-7 text-xs px-3" onClick={(e) => { e.stopPropagation(); handleUploadClick(type); }}>
                            <Upload className="h-3 w-3 mr-1.5" />Upload
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* CR-5320 + CR-5321: drill-down modal */}
      <ImportDetailModal
        open={!!detail}
        onClose={() => setDetail(null)}
        mode={detail?.mode || 'types'}
        steps={detail?.steps || []}
        stepFocus={detail?.stepFocus}
        importTypes={IMPORT_TYPES.map(t => ({ step: t.step, shortName: t.shortName, name: t.name }))}
        title={detail?.title || ''}
        description={detail?.description}
      />

      {/* Upload Dialog */}
      <Dialog open={!!uploadDialog} onOpenChange={(open) => { if (!open) { setUploadDialog(null); setSelectedFile(null); } }}>
        <DialogContent className="max-w-xl">
          {uploadDialog && (() => {
            const Icon = uploadDialog.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    Import {uploadDialog.name}
                  </DialogTitle>
                  <DialogDescription>{uploadDialog.description}</DialogDescription>
                </DialogHeader>

                <div className="space-y-5 py-2">
                  {/* Expected columns */}
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Expected Excel Columns</p>
                    <div className="flex flex-wrap gap-1.5">
                      {uploadDialog.columns.map(col => (
                        <Badge key={col} variant="secondary" className="text-xs font-normal">{col}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Site selector */}
                  {uploadDialog.siteRequired && sites.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Target Company <span className="text-red-500">*</span></label>
                      <p className="text-xs text-muted-foreground">This data will be imported for the selected company</p>
                      <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select company..." />
                        </SelectTrigger>
                        <SelectContent>
                          {sites.map(site => (
                            <SelectItem key={site.id} value={String(site.id)}>{site.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Hidden file input — outside drop zone to avoid event conflicts */}
                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} className="hidden" />

                  {/* Drop zone */}
                  <div
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      dragActive ? 'border-primary bg-primary/5 scale-[1.01]' :
                      selectedFile ? 'border-green-400 bg-green-50/50' :
                      'border-gray-200 hover:border-gray-300 hover:bg-muted/20'
                    }`}
                    onDragOver={e => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={handleDrop}
                  >
                    {selectedFile ? (
                      <div className="space-y-2">
                        <div className="w-14 h-14 mx-auto bg-green-100 rounded-xl flex items-center justify-center">
                          <FileSpreadsheet className="h-7 w-7 text-green-600" />
                        </div>
                        <p className="font-semibold">{selectedFile.name}</p>
                        <p className="text-sm text-muted-foreground">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" />Remove file
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="w-14 h-14 mx-auto bg-muted rounded-xl flex items-center justify-center">
                          <Upload className="h-7 w-7 text-muted-foreground" />
                        </div>
                        <p className="font-medium">Drop your Excel file here</p>
                        <p className="text-sm text-muted-foreground mb-3">Supports .xlsx, .xls, and .csv files</p>
                        <Button variant="outline" size="sm" type="button" onClick={() => fileInputRef.current?.click()}>
                          <FileSpreadsheet className="h-4 w-4 mr-2" />Browse Files
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Previous errors */}
                  {stepResults[uploadDialog.step]?.errors?.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-semibold text-amber-800 mb-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                        Previous import had {stepResults[uploadDialog.step].errors.length} error(s):
                      </p>
                      <div className="text-xs text-amber-700 max-h-24 overflow-y-auto space-y-0.5">
                        {stepResults[uploadDialog.step].errors.slice(0, 5).map((err, i) => (
                          <p key={i}>• {err}</p>
                        ))}
                        {stepResults[uploadDialog.step].errors.length > 5 && (
                          <p className="font-medium">...and {stepResults[uploadDialog.step].errors.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => { setUploadDialog(null); setSelectedFile(null); }}>Cancel</Button>
                  <Button
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading !== null || (uploadDialog.siteRequired && !selectedSiteId)}
                    className="min-w-[140px]"
                  >
                    {uploading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" />Upload & Import</>
                    )}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
